import { ClinicalGuides } from '../App';

export const toBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix e.g. "data:image/png;base64,"
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const dataUrlToParts = (dataUrl: string): { base64: string, mimeType: string } | null => {
    const match = dataUrl.match(/^data:(.*?);base64,(.*)$/);
    if (!match || match.length < 3) return null;
    return {
        mimeType: match[1],
        base64: match[2]
    };
};

export const padImageToRatio = (imageUrl: string, targetRatio: number): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const sourceWidth = img.naturalWidth;
            const sourceHeight = img.naturalHeight;

            let targetWidth, targetHeight;

            if (sourceWidth / sourceHeight > targetRatio) { // Image is wider than target
                targetWidth = sourceWidth;
                targetHeight = sourceWidth / targetRatio;
            } else { // Image is taller or same AR as target
                targetHeight = sourceHeight;
                targetWidth = sourceHeight * targetRatio;
            }

            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject('Could not get canvas context');

            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, targetWidth, targetHeight);

            const offsetX = (targetWidth - sourceWidth) / 2;
            const offsetY = (targetHeight - sourceHeight) / 2;
            ctx.drawImage(img, offsetX, offsetY);

            resolve(canvas.toDataURL('image/jpeg'));
        };
        img.onerror = reject;
        img.src = imageUrl;
    });
};

export const drawMouthAnnotation = (
  baseImageUrl: string, 
  normalizedPath: { x: number; y: number }[]
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Could not get canvas context');

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Draw filled overlay path
      if (normalizedPath.length > 2) { // Need at least 3 points for a fillable area
        ctx.beginPath();
        // Un-normalize points
        const firstPoint = normalizedPath[0];
        ctx.moveTo(firstPoint.x * img.naturalWidth, firstPoint.y * img.naturalHeight);
        
        for (let i = 1; i < normalizedPath.length; i++) {
          const point = normalizedPath[i];
          ctx.lineTo(point.x * img.naturalWidth, point.y * img.naturalHeight);
        }
        
        ctx.closePath();
        
        ctx.fillStyle = 'rgba(0, 255, 0, 0.1)'; // 10% opacity green
        ctx.fill();
      }

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = baseImageUrl;
  });
};

export const drawImageWithOverlay = (
  baseImageUrl: string, 
  normalizedPath: { x: number; y: number }[]
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Could not get canvas context');

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Draw overlay path
      if (normalizedPath.length > 1) {
        ctx.beginPath();
        // Un-normalize points
        ctx.moveTo(normalizedPath[0].x * img.naturalWidth, normalizedPath[0].y * img.naturalHeight);
        for (let i = 1; i < normalizedPath.length; i++) {
          ctx.lineTo(normalizedPath[i].x * img.naturalWidth, normalizedPath[i].y * img.naturalHeight);
        }
        ctx.strokeStyle = '#008000'; // Deep Green
        ctx.lineWidth = Math.max(5, img.naturalWidth * 0.004); // Line width scales with image size
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = 15;
        ctx.stroke();
      }

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = baseImageUrl;
  });
};

export const drawClinicalGuides = (
  baseImageUrl: string,
  guides: ClinicalGuides
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Could not get canvas context');

      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      const lineWidth = Math.max(3, img.naturalWidth * 0.003);
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 10;
      ctx.lineCap = 'round';

      const unnormalize = (p: {x:number, y:number}) => ({ x: p.x * img.naturalWidth, y: p.y * img.naturalHeight });

      // Draw midline (yellow, solid)
      if (guides.midline) {
        const start = unnormalize(guides.midline.start);
        const end = unnormalize(guides.midline.end);
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.strokeStyle = '#FFD700'; // Yellow
        ctx.lineWidth = lineWidth;
        ctx.setLineDash([]);
        ctx.stroke();
      }

      // Draw interpupillary (cyan, dashed)
      if (guides.interpupillary) {
        const start = unnormalize(guides.interpupillary.start);
        const end = unnormalize(guides.interpupillary.end);
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.strokeStyle = '#00FFFF'; // Cyan
        ctx.lineWidth = lineWidth;
        ctx.setLineDash([lineWidth * 3, lineWidth * 2]);
        ctx.stroke();
      }

      // Draw smile arc (magenta, solid)
      if (guides.smileArc) {
          const { p1, p2, p3 } = guides.smileArc;
          const cp1 = unnormalize(p1);
          const cp2 = unnormalize(p2);
          const cp3 = unnormalize(p3);
          
          // Use quadratic curve for a simple 3-point arc
          // The middle point acts as the control point.
          ctx.beginPath();
          ctx.moveTo(cp1.x, cp1.y);
          ctx.quadraticCurveTo(cp2.x, cp2.y, cp3.x, cp3.y);
          ctx.strokeStyle = '#FF00FF'; // Magenta
          ctx.lineWidth = lineWidth;
          ctx.setLineDash([]);
          ctx.stroke();
      }

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = baseImageUrl;
  });
};
